import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Copy, Download, Play, RefreshCw, Shield, Sparkles, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { api } from '../../lib/api';
import { getApiBase } from '../../lib/apiBase';
import { launcherInstallHint, probeLocalLauncher, wakeLocalConnector } from '../../lib/localConnectorLauncher';

function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function renderSimpleMarkdown(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      return (
        <h4 key={`h-${i}`} className="mt-3 first:mt-0 text-[11px] font-semibold uppercase tracking-wide text-violet-200">
          {trimmed.slice(3)}
        </h4>
      );
    }
    if (trimmed.startsWith('- ')) {
      const body = trimmed.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <p
          key={`b-${i}`}
          className="ml-2 border-l border-violet-500/30 pl-2 text-[11px] leading-relaxed text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      );
    }
    const num = trimmed.match(/^(\d+)\.\s+\*\*(.*?)\*\*\s*[—–-]\s*(.*)$/);
    if (num) {
      return (
        <div key={`n-${i}`} className="mt-2 flex gap-2 rounded-md border border-border/50 bg-background/40 px-2 py-1.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-500/25 text-[10px] font-bold text-violet-100">
            {num[1]}
          </span>
          <p className="text-[11px] leading-relaxed text-foreground">
            <span className="font-medium">{num[2]}</span>
            <span className="text-muted-foreground"> — {num[3]}</span>
          </p>
        </div>
      );
    }
    if (!trimmed) return <div key={`e-${i}`} className="h-1" />;
    const inline = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>');
    return (
      <p
        key={`p-${i}`}
        className="text-[11px] leading-relaxed text-muted-foreground"
        dangerouslySetInnerHTML={{ __html: inline }}
      />
    );
  });
}

const DEFAULT_SAFETY = {
  does_not: [
    'Touch lab target PCs or change their OS settings.',
    'Modify system-wide firewall or security policies.',
  ],
  does: [
    'Install under %LOCALAPPDATA%\\AERTA\\lab-connector (your user profile only).',
    'Set user-level AERTA_CONNECTOR_* environment variables.',
    'Add two logon Scheduled Tasks for launcher + connector.',
  ],
};

export default function AertagoConnectorSetupPanel({
  connectorOnline,
  onRefreshConfig,
  onAutomateLab,
  labBootstrapBusy,
}) {
  const [launcherOk, setLauncherOk] = useState(null);
  const [prepareBusy, setPrepareBusy] = useState(false);
  const [assistBusy, setAssistBusy] = useState(false);
  const [guide, setGuide] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const [safetyInfo, setSafetyInfo] = useState(DEFAULT_SAFETY);
  const [labCidr, setLabCidr] = useState('');
  const [installReady, setInstallReady] = useState(false);
  const [polling, setPolling] = useState(false);

  const probe = useCallback(async () => {
    const r = await probeLocalLauncher();
    setLauncherOk(r.ok);
    return r;
  }, []);

  useEffect(() => {
    probe();
    const t = window.setInterval(probe, 8000);
    return () => window.clearInterval(t);
  }, [probe]);

  const downloadInstaller = useCallback(async () => {
    setPrepareBusy(true);
    try {
      const res = await api.post(
        '/api/dojo-lab/aertago-control/connector-install/prepare',
        {
          lab_cidr: labCidr.trim() || undefined,
          request_api_base: getApiBase(),
        },
        { timeoutMs: 60000 }
      );
      if (!res?.ok) {
        throw new Error(res?.message || 'Could not prepare installer.');
      }
      if (res.installer_safety) {
        setSafetyInfo(res.installer_safety);
      }
      const ps1Name = res.install_script_filename || 'AERTA-Install-Operator-Stack.ps1';
      downloadTextFile(ps1Name, res.install_script || '');
      if (res.install_bat) {
        downloadTextFile(res.install_bat_filename || 'AERTA-Install-Operator-Stack.bat', res.install_bat);
      }
      setInstallReady(true);
      toast.success('Download both files — run the .bat first (keeps window open).');
    } catch (e) {
      toast.error(e.message || 'Install prepare failed');
    } finally {
      setPrepareBusy(false);
    }
  }, [labCidr]);

  const runAssist = useCallback(async () => {
    setAssistBusy(true);
    setShowGuide(true);
    try {
      const res = await api.post(
        '/api/dojo-lab/aertago-control/connector-install/assist',
        { focus: 'Premium guided install for Windows operator PC' },
        { timeoutMs: 90000 }
      );
      const g = res?.assist || res?.guide;
      if (g) {
        setGuide(g);
      } else {
        throw new Error('No guide returned');
      }
    } catch (e) {
      toast.error(e.message || 'Setup guide failed');
    } finally {
      setAssistBusy(false);
    }
  }, []);

  const copyGuide = useCallback(() => {
    if (!guide?.narrative) return;
    navigator.clipboard?.writeText(guide.narrative);
    toast.success('Guide copied');
  }, [guide]);

  const continueAfterInstall = useCallback(async () => {
    setPolling(true);
    try {
      await wakeLocalConnector();
      for (let i = 0; i < 30; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const r = await probe();
        if (r.ok) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, 1500));
      }
      if (onRefreshConfig) await onRefreshConfig({ silent: true });
      if (onAutomateLab) await onAutomateLab();
    } finally {
      setPolling(false);
    }
  }, [onAutomateLab, onRefreshConfig, probe]);

  const phase =
    connectorOnline === true
      ? 'ready'
      : launcherOk
        ? 'launcher_only'
        : installReady
          ? 'awaiting_run'
          : 'needs_install';

  return (
    <div className="overflow-hidden rounded-xl border border-violet-500/35 bg-gradient-to-br from-violet-500/[0.12] via-background/80 to-background/90 shadow-lg shadow-violet-950/20">
      <div className="border-b border-violet-500/25 bg-violet-500/[0.06] px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/20">
            <Wifi className="h-4 w-4 text-violet-300" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold tracking-tight text-foreground">Operator stack</p>
            <p className="text-[10px] text-muted-foreground">Automated install · remote start from this browser</p>
          </div>
          <Badge variant={connectorOnline ? 'success' : launcherOk ? 'warning' : 'secondary'}>
            {connectorOnline ? 'Online' : launcherOk ? 'Launcher' : 'Not installed'}
          </Badge>
        </div>
      </div>

      <div className="space-y-3 p-4 text-[11px] leading-relaxed">
        <p className="text-muted-foreground">
          Download a personalized installer — env vars and API token are filled in automatically. Does not modify lab
          target machines.
        </p>

        <details
          className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.05]"
          open={showSafety}
          onToggle={(e) => setShowSafety(e.target.open)}
        >
          <summary className="cursor-pointer list-none px-3 py-2 font-medium text-emerald-200/90">
            <span className="inline-flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" />
              Will this harm my PC? (scope & uninstall)
            </span>
          </summary>
          <div className="space-y-2 border-t border-emerald-500/20 px-3 py-2 text-[10px] text-muted-foreground">
            <p className="text-emerald-100/90">
              No — it does not “corrupt” your system. It installs a small AERTA lab agent under your user account, similar
              to a VPN or dev tool. You can remove it completely.
            </p>
            <p className="font-medium text-foreground">It does not:</p>
            <ul className="list-disc pl-4">
              {(safetyInfo.does_not || DEFAULT_SAFETY.does_not).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            <p className="font-medium text-foreground">It only:</p>
            <ul className="list-disc pl-4">
              {(safetyInfo.does || DEFAULT_SAFETY.does).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {safetyInfo.uninstall_hint ? (
              <p className="rounded border border-border/50 bg-background/50 p-2 font-mono text-[9px]">
                Uninstall: {safetyInfo.uninstall_hint}
              </p>
            ) : null}
          </div>
        </details>

        {phase === 'needs_install' || phase === 'awaiting_run' ? (
          <label className="block text-[10px] text-muted-foreground">
            Lab CIDR (optional)
            <input
              className="mt-1 w-full max-w-md rounded-md border border-border/70 bg-background/80 px-2 py-1.5 font-mono text-[10px]"
              value={labCidr}
              onChange={(e) => setLabCidr(e.target.value)}
              placeholder="Auto-detect from Wi‑Fi if empty"
            />
          </label>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" className="shadow-md shadow-violet-900/30" disabled={prepareBusy} onClick={downloadInstaller}>
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {prepareBusy ? 'Preparing…' : 'Download installer'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-violet-400/40 bg-violet-500/10 hover:bg-violet-500/20"
            disabled={assistBusy}
            onClick={runAssist}
          >
            <Sparkles className={`mr-1.5 h-3.5 w-3.5 ${assistBusy ? 'animate-pulse' : ''}`} />
            {assistBusy ? 'Preparing guide…' : 'AI setup guide'}
          </Button>
          {(installReady || launcherOk) && !connectorOnline ? (
            <Button type="button" size="sm" variant="secondary" disabled={polling || labBootstrapBusy} onClick={continueAfterInstall}>
              <Play className="mr-1.5 h-3.5 w-3.5" />
              {polling || labBootstrapBusy ? 'Finishing…' : 'Continue → automate lab'}
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" onClick={probe}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Recheck
          </Button>
        </div>

        {showGuide && (assistBusy || guide) ? (
          <div className="rounded-lg border border-violet-400/30 bg-gradient-to-b from-violet-500/[0.08] to-background/60 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold text-foreground">{guide?.headline || 'Setup guide'}</p>
                <p className="text-[10px] text-muted-foreground">{guide?.subtitle || 'Loading…'}</p>
              </div>
              <div className="flex items-center gap-1">
                {guide?.source === 'ai' ? (
                  <Badge variant="outline" className="border-violet-400/50 text-[9px] text-violet-200">
                    AI · {guide?.model || 'LLM'}
                  </Badge>
                ) : guide ? (
                  <Badge variant="secondary" className="text-[9px]">
                    Guided
                  </Badge>
                ) : null}
                {guide?.narrative ? (
                  <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={copyGuide}>
                    <Copy className="h-3 w-3" />
                  </Button>
                ) : null}
              </div>
            </div>
            {assistBusy ? (
              <div className="space-y-2 animate-pulse">
                <div className="h-3 w-3/4 rounded bg-muted/60" />
                <div className="h-3 w-full rounded bg-muted/40" />
                <div className="h-3 w-5/6 rounded bg-muted/40" />
              </div>
            ) : (
              <div className="max-h-[220px] overflow-y-auto pr-1">{renderSimpleMarkdown(guide?.narrative)}</div>
            )}
            {Array.isArray(guide?.steps) && guide.steps.length > 0 ? (
              <ul className="mt-3 space-y-1 border-t border-border/40 pt-2">
                {guide.steps.map((s, idx) => (
                  <li key={s.title} className="flex items-start gap-2 text-[10px]">
                    <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-violet-400" />
                    <span>
                      <span className="font-medium text-foreground">{s.title}</span>
                      <span className="text-muted-foreground"> — {s.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {phase === 'ready' ? (
          <p className="flex items-center gap-2 text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            Operator stack online — use Automate lab setup.
          </p>
        ) : phase === 'awaiting_run' ? (
          <p className="text-amber-200/90">
            Run <strong className="text-foreground">AERTA-Install-Operator-Stack.bat</strong> (not raw .ps1) — wait for
            Done + Press Enter, then click Continue.
          </p>
        ) : (
          <p className="text-[10px] text-muted-foreground">{launcherInstallHint()}</p>
        )}
      </div>
    </div>
  );
}
